import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ChevronRight, ListChecks } from 'lucide-react';

import { formatDate } from '../../utils/Utils';
import usePerformanceInbox, { KIND_META, FallbackIcon } from './config';

// P2 Faz 1 / Paket C — "Performans Gelen Kutum": tek ekranda bekleyen TÜM
// performans işleri (360° formu, hedef/revizyon kararı, aday onayı, kalibrasyon
// el sıkışması, İK kuyrukları, IDP onayı). DataTable DEĞİL, kart listesi —
// gelen kutusu hissi (MyPipPage/MyCalibrationPage'in Card sayfa dilini izler).
// Satıra tıklayınca ilgili sayfaya gidilir (BE'nin döndüğü `link`).
// Bileşende toast YOK (hata yolu axios interceptor'ının işi).
//
// Müşteri geri bildirimi (2026-10-28) — "karta tıklıyorum, amacı belli olmayan
// bir listeye gidiyor": BE'nin `link`'i artık KAYDA ÖZGÜ (form, karar modalı,
// onay diyaloğu doğrudan açılır — bkz. backend PerformanceInboxLinks) ve
// OTORİTERDİR (FE override YOK). Bağlantısı olmayan kart tıklanamaz: düğme
// anlamı, ok simgesi ve el imleci olmadan düz kart olarak çizilir. Başlığın
// altındaki tek satır sayfanın amacını ve kart tıklamasının ne yaptığını anlatır
// (Performansım > Görevlerim sekmesi de AYNI bileşeni gömer; orada da geçerli).

const TONES = {
  primary: (t) => t.colors.primary.main,
  info: (t) => t.colors.auxiliar.info,
  success: (t) => t.colors.auxiliar.success,
  warning: (t) => t.colors.auxiliar.warning,
  danger: (t) => t.colors.auxiliar.danger,
  purple: (t) => t.colors.auxiliar.purple,
};

const toneColor = (theme, tone) => (TONES[tone] || TONES.primary)(theme);

const Container = styled.div``;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const PageTitle = styled.h2`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PageHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const CountChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 14px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.dark};
  background: ${({ theme }) => `${theme.colors.primary.main}1A`};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface?.card || '#fff'};
  border: 1px solid ${({ theme }) => theme.colors.surface?.border || '#E8ECF0'};
  border-radius: 10px;
  padding: 20px 22px;
`;

// Tıklanabilir (bağlantılı) kart düğmedir; bağlantısız kart AYNI görünümde düz
// bir kutudur (düğme anlamı, imleç ve üzerine gelme efekti yok).
const itemCardBase = css`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  font-family: ${({ theme }) => theme.metrics.font.family};
  background: ${({ theme }) => theme.colors.surface?.card || '#fff'};
  border: 1px solid ${({ $overdue, theme }) => ($overdue ? `${theme.colors.auxiliar.danger}55` : (theme.colors.surface?.border || '#E8ECF0'))};
  border-left: 3px solid ${({ $tone, theme }) => toneColor(theme, $tone)};
  border-radius: 10px;
  padding: 14px 16px;
`;

const ItemCard = styled.div`
  ${itemCardBase}
  cursor: default;
`;

const ItemButton = styled.button`
  ${itemCardBase}
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.surface?.borderHover || '#E2E8F0'};
    border-left-color: ${({ $tone, theme }) => toneColor(theme, $tone)};
    box-shadow: 0 2px 10px rgba(${({ theme }) => theme.colors.shadow?.rgb || '0, 66, 66'}, 0.08);
    transform: translateY(-1px);
  }
`;

const IconTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  color: ${({ $tone, theme }) => toneColor(theme, $tone)};
  background: ${({ $tone, theme }) => `${toneColor(theme, $tone)}1A`};
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const KindLabel = styled.div`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $tone, theme }) => toneColor(theme, $tone)};
  margin-bottom: 2px;
`;

const Title = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const Subtitle = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  overflow-wrap: anywhere;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
`;

const DueBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  white-space: nowrap;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ $overdue, theme }) => ($overdue ? theme.colors.auxiliar.danger : theme.colors.text.dark.little)};
  background: ${({ $overdue, theme }) => ($overdue ? `${theme.colors.auxiliar.danger}1A` : theme.colors.bgColor)};
`;

const Chevron = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.text.light.little};
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 24px;
  text-align: center;
`;

const EmptyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.auxiliar.success};
  background: ${({ theme }) => `${theme.colors.auxiliar.success}1A`};
`;

const EmptyTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

export default function PerformanceInboxPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const { items, fetching } = usePerformanceInbox();

  const go = useCallback((link) => {
    if (link) history.push(link);
  }, [history]);

  return (
    <Container>
      <PageHeader>
        <TitleBlock>
          <PageTitle>{t('performance.inbox.title')}</PageTitle>
          <PageHint data-hint="inbox">{t('performance.inbox.hint')}</PageHint>
        </TitleBlock>
        {!fetching && items.length > 0 && (
          <CountChip>{t('performance.inbox.pendingCount', { count: items.length })}</CountChip>
        )}
      </PageHeader>

      {fetching && (
        <Card><Centered><EmptyText>{t('common.loading')}</EmptyText></Centered></Card>
      )}

      {!fetching && items.length === 0 && (
        <Card>
          <Centered>
            <EmptyIcon><ListChecks size={26} /></EmptyIcon>
            <EmptyTitle>{t('performance.inbox.empty')}</EmptyTitle>
            <EmptyText>{t('performance.inbox.emptyHint')}</EmptyText>
          </Centered>
        </Card>
      )}

      {!fetching && items.length > 0 && (
        <List>
          {items.map((item, index) => {
            const meta = KIND_META[item.kind] || {};
            const Icon = meta.Icon || FallbackIcon;
            const tone = meta.tone || 'primary';
            // BE'nin `link`'i otoriter ve kayda özgü (2026-10-28) — eski
            // KIND_META link override'ları (feedback_request, pm_action) BE
            // aynı hedefleri artık kendisi ürettiği için kaldırıldı.
            const link = item.link || null;
            // Skorlama Faz F, Task 5 — `goal_stale`: BE'nin `subtitle`'ı çıplak
            // bir gün sayısı (i18n YOK, task-3-report.md §1.3), `meta.subtitle`
            // VARSA onu kendi cümle şablonuna gömer.
            const subtitle = meta.subtitle ? meta.subtitle(item, t) : item.subtitle;
            const CardTag = link ? ItemButton : ItemCard;
            const cardProps = link ? { type: 'button', onClick: () => go(link) } : { 'data-static': '1' };

            return (
              <CardTag
                key={`${item.kind}-${index}`}
                $tone={tone}
                $overdue={!!item.overdue}
                {...cardProps}
              >
                <IconTile $tone={tone}><Icon size={19} /></IconTile>

                <Body>
                  <KindLabel $tone={tone}>
                    {t(`performance.inbox.kind.${item.kind}`, { defaultValue: item.kind })}
                  </KindLabel>
                  <Title>{item.title}</Title>
                  {subtitle && <Subtitle>{subtitle}</Subtitle>}
                </Body>

                <Meta>
                  {item.dueAt && (
                    <DueBadge $overdue={!!item.overdue}>
                      {item.overdue
                        ? t('performance.inbox.overdueOn', { date: formatDate(item.dueAt, lang) })
                        : t('performance.inbox.dueOn', { date: formatDate(item.dueAt, lang) })}
                    </DueBadge>
                  )}
                  {link ? <Chevron><ChevronRight size={18} /></Chevron> : null}
                </Meta>
              </CardTag>
            );
          })}
        </List>
      )}
    </Container>
  );
}
